import React, { useEffect, useState } from 'react';
import { X, Ticket, Calendar, DollarSign, User, Mail, Phone, ExternalLink } from 'lucide-react';
import { formatFirestoreTimestamp, formatCurrency, getLiveStreamUrls } from '../utils/common';
import { ordersCollection, eventsCollection } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

const ManageLiveStreamTicket = ({ ticket, handleClose }) => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [eventDetails, setEventDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                // Fetch order details
                if (ticket?.orderId) {
                    const orderDoc = await getDoc(doc(ordersCollection, ticket.orderId));
                    if (orderDoc.exists()) {
                        setOrderDetails({
                            id: orderDoc.id,
                            ...orderDoc.data()
                        });
                    }
                }

                // Fetch event details
                if (ticket?.eventId) {
                    const eventDoc = await getDoc(doc(eventsCollection, ticket.eventId));
                    if (eventDoc.exists()) {
                        setEventDetails({
                            id: eventDoc.id,
                            ...eventDoc.data()
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching details:', error);
                toast.error('Failed to fetch details');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [ticket?.orderId, ticket?.eventId]);

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`📋 Copied ${label} to Clipboard`);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { class: 'badge-warning', text: 'Pending' },
            'PAID': { class: 'badge-success', text: 'Paid' },
            'FAILED': { class: 'badge-error', text: 'Failed' },
            'CANCELLED': { class: 'badge-neutral', text: 'Cancelled' }
        };

        const config = statusConfig[status] || { class: 'badge-neutral', text: status };
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    return (
        <div className="flex-center justify-start py-4 px-6 flex-1 md:border-l-2 border-current gap-4 w-full">
            <div className="w-full flex flex-row items-center gap-2">
                <Ticket />
                <p className="font-bold text-xl">Live Stream Ticket</p>
                <div className="flex-1" />
                <button
                    className="btn btn-outline btn-square btn-sm"
                    onClick={handleClose}
                >
                    <X />
                </button>
            </div>

            {/* Ticket Code */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">
                    Ticket Code:
                </label>
                <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                    <input
                        className="w-full px-4 font-mono text-lg"
                        value={ticket?.code || ""}
                        readOnly
                    />
                    <button
                        className="btn btn-ghost btn-square"
                        onClick={() =>
                            copyToClipboard(ticket?.code, "Ticket Code")
                        }
                    >
                        <ExternalLink size={18} />
                    </button>
                </div>
            </div>

            {/* Live Stream Access URL */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">
                    Live Stream Access URL:
                </label>
                <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                    <input
                        className="w-full px-4 font-mono text-sm"
                        value={`https://malaysiafightleague.com/event/${ticket?.eventId}?liveStreamTicketCode=${ticket?.code}`}
                        readOnly
                    />
                    <a
                        href={`https://malaysiafightleague.com/event/${ticket?.eventId}?liveStreamTicketCode=${ticket?.code}`}
                        target='_blank'
                    >
                        <button
                            className="btn btn-ghost btn-square"
                            // onClick={() => copyToClipboard(`https://malaysiafightleague.com/event/${ticket?.eventId}?liveStreamTicketCode=${ticket?.code}`, 'Live Stream URL')}
                        >
                            <ExternalLink size={18} />
                        </button>
                    </a>
                </div>
            </div>

            {/* Ticket Status */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">Status:</label>
                <div className="flex-center">
                    <span
                        className={`badge ${
                            ticket?.isActivated
                                ? "badge-success"
                                : "badge-warning"
                        } text-lg`}
                    >
                        {ticket?.isActivated ? "Active" : "Inactive"}
                    </span>
                </div>
            </div>

            {/* Event ID */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">Event ID:</label>
                <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                    <input
                        className="w-full px-4 font-mono text-sm"
                        value={ticket?.eventId || ""}
                        readOnly
                    />
                    <button
                        className="btn btn-ghost btn-square"
                        onClick={() =>
                            copyToClipboard(ticket?.eventId, "Event ID")
                        }
                    >
                        <ExternalLink size={18} />
                    </button>
                </div>
            </div>

            {/* Order ID */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">Order ID:</label>
                <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                    <input
                        className="w-full px-4 font-mono text-sm"
                        value={ticket?.orderId || ""}
                        readOnly
                    />
                    <button
                        className="btn btn-ghost btn-square"
                        onClick={() =>
                            copyToClipboard(ticket?.orderId, "Order ID")
                        }
                    >
                        <ExternalLink size={18} />
                    </button>
                </div>
            </div>

            {/* Timestamps */}
            <div className="w-full">
                <label className="w-full font-xs opacity-60">Created At:</label>
                <input
                    className="input input-bordered w-full"
                    value={formatFirestoreTimestamp(ticket?.createdAt) || "N/A"}
                    readOnly
                />
            </div>

            <div className="w-full">
                <label className="w-full font-xs opacity-60">Updated At:</label>
                <input
                    className="input input-bordered w-full"
                    value={formatFirestoreTimestamp(ticket?.updateAt) || "N/A"}
                    readOnly
                />
            </div>

            {/* Order Details Section */}
            {orderDetails && (
                <>
                    <div className="divider">Order Details</div>

                    {/* Order Status */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Order Status:
                        </label>
                        <div className="flex-center">
                            {getStatusBadge(orderDetails.status)}
                        </div>
                    </div>

                    {/* Customer Name */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Customer Name:
                        </label>
                        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                            <input
                                className="w-full px-4"
                                value={orderDetails.name || ""}
                                readOnly
                            />
                            <button
                                className="btn btn-ghost btn-square"
                                onClick={() =>
                                    copyToClipboard(
                                        orderDetails.name,
                                        "Customer Name"
                                    )
                                }
                            >
                                <User size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Email */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Email:
                        </label>
                        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                            <input
                                className="w-full px-4"
                                value={orderDetails.email || ""}
                                readOnly
                            />
                            <a
                                className="btn btn-ghost btn-square"
                                href={`mailto:${orderDetails.email}`}
                            >
                                <Mail size={18} />
                            </a>
                        </div>
                    </div>

                    {/* Phone Number */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Phone Number:
                        </label>
                        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                            <input
                                className="w-full px-4"
                                value={orderDetails.phoneNumber || ""}
                                readOnly
                            />
                            <a
                                className="btn btn-ghost btn-square"
                                href={`tel:${orderDetails.phoneNumber}`}
                            >
                                <Phone size={18} />
                            </a>
                        </div>
                    </div>

                    {/* Price */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Price:
                        </label>
                        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                            <input
                                className="w-full px-4"
                                value={formatCurrency(orderDetails.price || 0)}
                                readOnly
                            />
                            <button className="btn btn-ghost btn-square">
                                <DollarSign
                                    size={18}
                                    className="text-success"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Title:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={orderDetails.title || ""}
                            readOnly
                        />
                    </div>

                    {/* Order Created At */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Order Created:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={
                                formatFirestoreTimestamp(
                                    orderDetails.createdAt
                                ) || "N/A"
                            }
                            readOnly
                        />
                    </div>

                    {/* Order Updated At */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Order Updated:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={
                                formatFirestoreTimestamp(
                                    orderDetails.updateAt
                                ) || "N/A"
                            }
                            readOnly
                        />
                    </div>
                </>
            )}

            {/* Event Details Section */}
            {eventDetails && (
                <>
                    <div className="divider">Event Details</div>

                    {/* Event Name */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Name:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={eventDetails.name || ""}
                            readOnly
                        />
                    </div>

                    {/* Event Date */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Date:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={
                                formatFirestoreTimestamp(
                                    eventDetails.dateTime
                                ) || "N/A"
                            }
                            readOnly
                        />
                    </div>

                    {/* Venue */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Venue:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={eventDetails.venue || "N/A"}
                            readOnly
                        />
                    </div>

                    {/* Address */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Address:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={eventDetails.address || "N/A"}
                            readOnly
                        />
                    </div>

                    {/* Live Stream URLs */}
                    {getLiveStreamUrls(eventDetails.liveStreamUrl).length > 0 && (
                        <div className="w-full flex flex-col gap-2">
                            <label className="w-full font-xs opacity-60">
                                Live Stream URL
                                {getLiveStreamUrls(eventDetails.liveStreamUrl).length > 1
                                    ? "s"
                                    : ""}
                                :
                            </label>
                            {getLiveStreamUrls(eventDetails.liveStreamUrl).map(
                                (url, i) => (
                                    <div
                                        key={i}
                                        className="input input-bordered flex items-center gap-2 w-full relative p-0"
                                    >
                                        <input
                                            className="w-full px-4 font-mono text-sm"
                                            value={url}
                                            readOnly
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-square"
                                            onClick={() =>
                                                copyToClipboard(
                                                    url,
                                                    getLiveStreamUrls(eventDetails.liveStreamUrl)
                                                        .length > 1
                                                        ? `Live Stream URL ${i + 1}`
                                                        : "Live Stream URL"
                                                )
                                            }
                                        >
                                            <ExternalLink size={18} />
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    {/* Live Stream Ticket Price */}
                    {eventDetails.liveStreamTicketPrice && (
                        <div className="w-full">
                            <label className="w-full font-xs opacity-60">
                                Live Stream Ticket Price:
                            </label>
                            <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
                                <input
                                    className="w-full px-4"
                                    value={formatCurrency(
                                        eventDetails.liveStreamTicketPrice
                                    )}
                                    readOnly
                                />
                                <DollarSign
                                    size={18}
                                    className="text-success"
                                />
                            </div>
                        </div>
                    )}

                    {/* Live Stream Ticket Purchase Deadline */}
                    {eventDetails.liveStreamTicketPurchaseDeadline && (
                        <div className="w-full">
                            <label className="w-full font-xs opacity-60">
                                Ticket Purchase Deadline:
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={
                                    formatFirestoreTimestamp(
                                        eventDetails.liveStreamTicketPurchaseDeadline
                                    ) || "N/A"
                                }
                                readOnly
                            />
                        </div>
                    )}

                    {/* Event Status */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Status:
                        </label>
                        <div className="flex-center">
                            <span
                                className={`badge ${
                                    eventDetails.status === "A"
                                        ? "badge-success"
                                        : "badge-warning"
                                }`}
                            >
                                {eventDetails.status === "A"
                                    ? "Active"
                                    : "Inactive"}
                            </span>
                        </div>
                    </div>

                    {/* Event Created At */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Created:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={
                                formatFirestoreTimestamp(
                                    eventDetails.createdAt
                                ) || "N/A"
                            }
                            readOnly
                        />
                    </div>

                    {/* Event Updated At */}
                    <div className="w-full">
                        <label className="w-full font-xs opacity-60">
                            Event Updated:
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={
                                formatFirestoreTimestamp(
                                    eventDetails.updateAt
                                ) || "N/A"
                            }
                            readOnly
                        />
                    </div>
                </>
            )}

            {/* Loading State */}
            {loading && (
                <div className="w-full flex-center">
                    <div className="loading loading-bars" />
                </div>
            )}

            {/* No Order Details */}
            {!loading && !orderDetails && ticket?.orderId && (
                <div className="w-full">
                    <div className="alert alert-warning">
                        <Calendar />
                        <span>Order details not found</span>
                    </div>
                </div>
            )}

            {/* No Event Details */}
            {!loading && !eventDetails && ticket?.eventId && (
                <div className="w-full">
                    <div className="alert alert-warning">
                        <Calendar />
                        <span>Event details not found</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageLiveStreamTicket;
